import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Image, VideoCamera, Sparkle, MagnifyingGlass, Check } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'uploads', label: 'Uploads', icon: Image },
  { id: 'avatars', label: 'Avatars', icon: Sparkle },
  { id: 'videos', label: 'Videos', icon: VideoCamera },
];

export function AssetPickerModal({ isOpen, onClose, onSelect, accept = 'image', title = 'Use My Assets' }) {
  const [activeTab, setActiveTab] = useState(accept === 'video' ? 'videos' : 'uploads');
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const fetchAssets = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/assets/all`);
        setAssets(data);
      } catch {}
      setLoading(false);
    };
    fetchAssets();
  }, [isOpen]);

  const getItems = () => {
    const items = assets[activeTab] || [];
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(a => {
      const name = a.original_name || a.style || a.product_name || '';
      return name.toLowerCase().includes(q);
    });
  };

  const getUrl = (asset) => {
    if (activeTab === 'uploads') return `${API_URL}${asset.url}`;
    if (activeTab === 'avatars') return `${API_URL}/api/characters/file/${asset.char_id}`;
    if (activeTab === 'videos') return asset.video_url ? `${API_URL}${asset.video_url}` : null;
    return null;
  };

  const isImageType = (asset) => {
    if (activeTab === 'avatars') return true;
    if (activeTab === 'uploads') return asset.file_type === 'image';
    return false;
  };

  const filteredTabs = TABS.filter(t => {
    if (accept === 'image') return t.id !== 'videos';
    if (accept === 'video') return t.id === 'videos' || t.id === 'uploads';
    return true;
  });

  const items = getItems();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[140] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-2xl max-h-[80vh] mx-4 rounded-2xl overflow-hidden flex flex-col"
          style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
          onClick={e => e.stopPropagation()}
          data-testid="asset-picker-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
            <h3 className="text-sm font-medium" style={{ color: '#f5f0e8' }}>{title}</h3>
            <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Tabs + Search */}
          <div className="flex items-center gap-3 px-5 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            <div className="flex gap-1">
              {filteredTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`asset-picker-tab-${tab.id}`}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium transition-all"
                  style={{
                    background: activeTab === tab.id ? 'rgba(200,240,96,0.08)' : 'transparent',
                    color: activeTab === tab.id ? '#c8f060' : 'rgba(245,240,232,0.35)',
                  }}
                >
                  <tab.icon size={13} />
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="relative ml-auto">
              <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2" size={12} style={{ color: 'rgba(245,240,232,0.25)' }} />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-7 pr-3 py-1 text-xs w-36 rounded-md"
                style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.07)', color: '#f5f0e8', outline: 'none' }}
              />
            </div>
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="grid grid-cols-4 gap-2">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="aspect-square rounded-lg animate-pulse" style={{ background: '#1a1a1a' }} />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-sm" style={{ color: 'rgba(245,240,232,0.3)' }}>No assets found</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {items.map((asset, i) => {
                  const url = getUrl(asset);
                  if (!url) return null;
                  return (
                    <button
                      key={asset.file_id || asset.char_id || asset.video_id || i}
                      onClick={() => { onSelect(url, asset); onClose(); }}
                      data-testid={`asset-pick-${asset.file_id || asset.char_id || asset.video_id || i}`}
                      className="group relative aspect-square rounded-lg overflow-hidden transition-all hover:ring-2 hover:ring-[#c8f060]/50"
                      style={{ background: '#0a0a0a' }}
                    >
                      {isImageType(asset) ? (
                        <img src={url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <video src={url} className="w-full h-full object-cover" muted />
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#c8f060' }}>
                          <Check size={14} weight="bold" className="text-black" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
